import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import { useAccessibility } from '../../contexts/AccessibilityContext';

interface HitoriPuzzle {
  grid: number[][];
  solution: boolean[][];
  seed: number;
  size: number;
}

type CellState = null | boolean; // null = normal, false = black, true = circle

export default function HitoriGame() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { highContrast } = useAccessibility();

  // Parámetros URL
  const difficulty = parseInt(searchParams.get('dificultad') || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('hitori', difficulty);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();
  const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : null;

  // Estado del juego
  const [puzzle, setPuzzle] = useState<HitoriPuzzle | null>(null);
  const [userState, setUserState] = useState<CellState[][]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Estado UI
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moves, setMoves] = useState(0);
  const [solved, setSolved] = useState(false);
  const [victoryModalOpen, setVictoryModalOpen] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);

  // Historial para deshacer/rehacer
  const [history, setHistory] = useState<CellState[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Dificultades (mapeo a tamaños)
  const difficulties = [
    { id: 1, nombre: 'Neurona', color: '#22c55e', icon: '🧠', size: 5 },
    { id: 2, nombre: 'Sinapsis', color: '#3b82f6', icon: '⚡', size: 6 },
    { id: 3, nombre: 'Cerebro', color: '#a855f7', icon: '🧩', size: 8 },
    { id: 4, nombre: 'Genio', color: '#ef4444', icon: '💡', size: 9 },
    { id: 5, nombre: 'Einstein', color: '#f97316', icon: '🎓', size: 10 },
  ];

  const currentDifficulty = difficulties.find(d => d.id === difficulty) || difficulties[0];

  // Medición del tablero
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 8);
      setBoardH(clientHeight - 8);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);

  // Timer
  useEffect(() => {
    if (solved) return;
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [solved, startTime]);

  // Cargar puzzle al montar o cambiar dificultad
  useEffect(() => {
    generateNewPuzzle();
  }, [difficulty]);

  async function generateNewPuzzle(forceNew = false) {
    try {
      setLoading(true);
      setLoadingProgress(0);

      await updateProgress(20, 'Generando patrón...');

      const response = await api.get('/hitori/generate', {
        params: {
          size: currentDifficulty.size,
          ...(!forceNew && seedParam && { seed: seedParam })
        }
      });

      await updateProgress(60, 'Rellenando números...');

      const data = response.data.data as HitoriPuzzle;

      await updateProgress(80, 'Verificando...');

      setPuzzle(data);
      initializeGame(data.size);

      // Actualizar URL con seed
      const newParams = new URLSearchParams(searchParams);
      newParams.set('seed', data.seed.toString());
      setSearchParams(newParams, { replace: true });

      await updateProgress(100, '¡Listo!');

      setTimeout(() => {
        setLoading(false);
      }, 300);
    } catch (error) {
      console.error('Error generando puzzle:', error);
      toast.error('Error al generar puzzle');
      setLoading(false);
    }
  }

  async function updateProgress(percent: number, _text: string) {
    setLoadingProgress(percent);
    return new Promise(resolve => setTimeout(resolve, 100));
  }

  function initializeGame(size: number) {
    const initialState: CellState[][] = Array(size).fill(null).map(() =>
      Array(size).fill(null)
    );
    setUserState(initialState);
    setHistory([initialState.map(row => [...row])]);
    setHistoryIndex(0);
    setMoves(0);
    setSolved(false);
  }

  function handleCellClick(r: number, c: number) {
    if (solved || !puzzle) return;

    const newState = userState.map(row => [...row]);
    const currentState = newState[r][c];

    // Cycle: null -> black -> circle -> null
    if (currentState === null) {
      newState[r][c] = false; // black
    } else if (currentState === false) {
      newState[r][c] = true; // circle
    } else {
      newState[r][c] = null; // normal
    }

    setUserState(newState);
    setMoves(m => m + 1);
    saveToHistory(newState);

    // Auto-check for victory
    checkVictory(newState, false);
  }

  function saveToHistory(state: CellState[][]) {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(state.map(row => [...row]));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }

  function undo() {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setUserState(history[historyIndex - 1].map(row => [...row]));
    }
  }

  function redo() {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setUserState(history[historyIndex + 1].map(row => [...row]));
    }
  }

  function reset() {
    if (window.confirm('¿Reiniciar el tablero?')) {
      if (puzzle) {
        initializeGame(puzzle.size);
      }
    }
  }

  function showSolution() {
    if (!puzzle || !window.confirm('¿Ver la solución?')) return;

    const solutionState: CellState[][] = puzzle.solution.map(row =>
      row.map(cell => cell ? null : false)
    );
    setUserState(solutionState);
    saveToHistory(solutionState);
    toast.info('Solución revelada');
  }

  function giveHint() {
    if (!puzzle) return;

    // 1. Check for errors (user marked black but should be white)
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (userState[r][c] === false && puzzle.solution[r][c] === true) {
          const newState = userState.map(row => [...row]);
          newState[r][c] = null;
          setUserState(newState);
          saveToHistory(newState);
          toast.error('Corrección: Esta casilla no debe ser negra');
          return;
        }
      }
    }

    // 2. Reveal a black cell
    const candidates: { r: number; c: number }[] = [];
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (puzzle.solution[r][c] === false && userState[r][c] !== false) {
          candidates.push({ r, c });
        }
      }
    }

    if (candidates.length > 0) {
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      const newState = userState.map(row => [...row]);
      newState[pick.r][pick.c] = false;
      setUserState(newState);
      saveToHistory(newState);
      toast.success('Pista: Esta casilla debe ser negra');
    } else {
      toast.success('¡Parece que tienes todas las negras correctas!');
    }
  }

  function checkVictory(state: CellState[][], manual: boolean = false) {
    if (!puzzle || solved) return;

    // 1. Check no adjacent blacks
    for (let r = 0; r < puzzle.size; r++) {
      for (let c = 0; c < puzzle.size; c++) {
        if (state[r][c] === false) {
          if (c + 1 < puzzle.size && state[r][c + 1] === false) {
            if (manual) toast.error('Hay celdas negras adyacentes');
            return;
          }
          if (r + 1 < puzzle.size && state[r + 1][c] === false) {
            if (manual) toast.error('Hay celdas negras adyacentes');
            return;
          }
        }
      }
    }

    // 2. Check connectivity
    if (!checkConnectivity(state, puzzle.size)) {
      if (manual) toast.error('Las celdas blancas no están todas conectadas');
      return;
    }

    // 3. Check no duplicates in rows/cols
    for (let r = 0; r < puzzle.size; r++) {
      const seen = new Map<number, number>();
      for (let c = 0; c < puzzle.size; c++) {
        if (state[r][c] !== false) {
          const val = puzzle.grid[r][c];
          if (seen.has(val)) {
            if (manual) toast.error(`Hay números duplicados en la fila ${r + 1}`);
            return;
          }
          seen.set(val, c);
        }
      }
    }

    for (let c = 0; c < puzzle.size; c++) {
      const seen = new Map<number, number>();
      for (let r = 0; r < puzzle.size; r++) {
        if (state[r][c] !== false) {
          const val = puzzle.grid[r][c];
          if (seen.has(val)) {
            if (manual) toast.error(`Hay números duplicados en la columna ${c + 1}`);
            return;
          }
          seen.set(val, r);
        }
      }
    }

    // Victory!
    setSolved(true);
    setVictoryModalOpen(true);
    toast.success('¡Puzzle completado!');

    // Save progress (no await para no bloquear)
    saveProgress().catch(err => {
      console.error('Error en saveProgress:', err);
    });
  }

  function checkConnectivity(state: CellState[][], size: number): boolean {
    const whiteCells: { r: number; c: number }[] = [];
    let startNode: { r: number; c: number } | null = null;

    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (state[r][c] !== false) {
          const node = { r, c };
          whiteCells.push(node);
          if (!startNode) startNode = node;
        }
      }
    }

    if (whiteCells.length === 0) return true;
    if (!startNode) return false;

    const visited = new Set<string>();
    const stack = [startNode];
    visited.add(`${startNode.r},${startNode.c}`);
    let count = 0;

    while (stack.length > 0) {
      const { r, c } = stack.pop()!;
      count++;

      const deltas = [[0, 1], [0, -1], [1, 0], [-1, 0]];
      for (const [dr, dc] of deltas) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr >= 0 && nr < size && nc >= 0 && nc < size) {
          const key = `${nr},${nc}`;
          if (state[nr][nc] !== false && !visited.has(key)) {
            visited.add(key);
            stack.push({ r: nr, c: nc });
          }
        }
      }
    }

    return count === whiteCells.length;
  }

  async function saveProgress() {
    if (!user || !puzzle) {
      return;
    }

    try {
      // Guardar progreso normal (siempre)
      await saveGameProgress({
        tablero_id: puzzle.seed,
        tiempo: elapsedTime,
        movimientos: moves,
        completado: true,
      });

      // Si viene de una sesión, guardar también en la sesión
      await saveSessionResult({
        tiempo: elapsedTime,
        movimientos: moves,
        completado: true,
        datos_juego: {
          tablero_id: puzzle.seed,
          size: puzzle.size,
        },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
      // No lanzar el error para evitar romper la UI
    }
  }

  // ─── Modo asistido ────────────────────────────────────────────────────────
  function calcularErrores(state: CellState[][], grid: number[][], sz: number): Set<string> {
    const errores = new Set<string>();

    // Negras adyacentes
    for (let r = 0; r < sz; r++) {
      for (let c = 0; c < sz; c++) {
        if (state[r][c] === false) {
          if (c + 1 < sz && state[r][c + 1] === false) {
            errores.add(`${r},${c}`); errores.add(`${r},${c + 1}`);
          }
          if (r + 1 < sz && state[r + 1][c] === false) {
            errores.add(`${r},${c}`); errores.add(`${r + 1},${c}`);
          }
        }
      }
    }

    // Duplicados en filas
    for (let r = 0; r < sz; r++) {
      const seen = new Map<number, number[]>();
      for (let c = 0; c < sz; c++) {
        if (state[r][c] === false) continue;
        const v = grid[r][c];
        if (!seen.has(v)) seen.set(v, []);
        seen.get(v)!.push(c);
      }
      for (const cols of seen.values()) {
        if (cols.length > 1) cols.forEach(c => errores.add(`${r},${c}`));
      }
    }

    // Duplicados en columnas
    for (let c = 0; c < sz; c++) {
      const seen = new Map<number, number[]>();
      for (let r = 0; r < sz; r++) {
        if (state[r][c] === false) continue;
        const v = grid[r][c];
        if (!seen.has(v)) seen.set(v, []);
        seen.get(v)!.push(r);
      }
      for (const rows of seen.values()) {
        if (rows.length > 1) rows.forEach(r => errores.add(`${r},${c}`));
      }
    }

    return errores;
  }

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex items-center justify-center p-4">
        <div className="glass-container max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 border-4 border-neureka-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Generando puzzle...</h2>
            <p className="text-white/70">Preparando tu tablero Hitori</p>
          </div>
          <div className="w-full bg-white/10 rounded-full h-6 overflow-hidden">
            <div
              className="bg-neureka-yellow h-full transition-all duration-300 flex items-center justify-center text-sm font-bold"
              style={{ width: `${loadingProgress}%` }}
            >
              {loadingProgress > 10 && `${loadingProgress}%`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex items-center justify-center">
        <div className="glass-container max-w-md text-center">
          <p className="text-xl">Error cargando puzzle</p>
          <button onClick={() => navigate('/juegos')} className="btn-neureka mt-4">
            Volver a juegos
          </button>
        </div>
      </div>
    );
  }

  const difficultyOptions = [
    { orden: 1, nombre: 'Neurona', icono: '🧠' },
    { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
    { orden: 3, nombre: 'Cerebro', icono: '🧩' },
    { orden: 4, nombre: 'Genio', icono: '💡' },
    { orden: 5, nombre: 'Einstein', icono: '🎓' },
  ];

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Compact top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">⬛ Hitori</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficultyOptions.map(d => (
            <button
              key={d.orden}
              onClick={() => {
                const newParams = new URLSearchParams();
                newParams.set('dificultad', d.orden.toString());
                setSearchParams(newParams);
              }}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                difficulty === d.orden
                  ? 'bg-white text-purple-700'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex gap-1 items-center">
          {/* Undo */}
          <button onClick={undo} disabled={historyIndex <= 0} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Redo */}
          <button onClick={redo} disabled={historyIndex >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Hint */}
          <button onClick={giveHint} disabled={solved} title="Pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
          {/* Solution */}
          <button onClick={showSolution} disabled={solved} title="Ver solucion"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* Reset */}
          <button onClick={reset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* New board */}
          <button onClick={() => { setSearchParams({ dificultad: difficulty.toString() }); generateNewPuzzle(true); }} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* How to play */}
          <button onClick={() => setShowHowToPlay(true)} title="Como se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-black/10 px-3 py-1 flex items-center gap-4 text-white/80 text-xs">
        <span>{currentDifficulty.icon} {currentDifficulty.nombre} — {puzzle.size}×{puzzle.size}</span>
        <span>⏱ {formatTime(elapsedTime)}</span>
        <span>🎯 {moves} mov</span>
      </div>

      {/* Board Area */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-2">
        {boardW > 0 && boardH > 0 && (() => {
          const size = puzzle.size;
          const errores = modoAsistido ? calcularErrores(userState, puzzle.grid, size) : new Set<string>();
          const gap = 2;
          const pad = 4;
          const cellW = Math.floor((boardW - pad * 2 - gap * (size - 1)) / size);
          const cellH = Math.floor((boardH - pad * 2 - gap * (size - 1)) / size);
          const minCell = Math.min(cellW, cellH);
          const fontSize = Math.floor(minCell * 0.45);
          const circleSize = Math.floor(minCell * 0.62);

          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${size}, ${cellW}px)`,
                gridTemplateRows: `repeat(${size}, ${cellH}px)`,
                gap,
                backgroundColor: highContrast ? '#888888' : '#1f2937',
                padding: pad,
                borderRadius: 8,
              }}
            >
              {puzzle.grid.map((row, r) =>
                row.map((num, c) => {
                  const state = userState[r][c];
                  const hasError = errores.has(`${r},${c}`);
                  return (
                    <div
                      key={`${r}-${c}`}
                      onClick={() => handleCellClick(r, c)}
                      style={{
                        width: cellW,
                        height: cellH,
                        fontSize,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        userSelect: 'none',
                        position: 'relative',
                        transition: 'filter 0.1s',
                        backgroundColor: hasError && state !== false
                          ? (highContrast ? '#ff4444' : '#fca5a5')
                          : state === false ? (highContrast ? '#000000' : '#1f2937') : '#ffffff',
                        color: state === false ? '#ffffff' : '#111827',
                        outline: hasError ? `3px solid ${highContrast ? '#ff0000' : '#ef4444'}` : undefined,
                        outlineOffset: '-2px',
                      }}
                      onMouseEnter={e => { if (state !== false) (e.currentTarget as HTMLElement).style.filter = 'brightness(0.93)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.filter = ''; }}
                    >
                      <span style={{ position: 'relative', zIndex: 1 }}>{num}</span>
                      {state === true && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <div style={{
                            width: circleSize, height: circleSize,
                            borderRadius: '50%',
                            border: `${Math.max(2, Math.floor(minCell * 0.08))}px solid ${highContrast ? '#0000ff' : '#3b82f6'}`,
                          }} />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          );
        })()}
      </div>

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Hitori"
        gameIcon="⬛"
        objective="Elimina los números repetidos pintándolos de negro, dejando solo un número de cada tipo en cada fila y columna."
        rules={[
          'No puede haber números repetidos en ninguna fila o columna (las celdas blancas)',
          'Las celdas negras NO pueden ser adyacentes horizontal o verticalmente',
          'Todas las celdas blancas deben estar conectadas entre sí (no pueden formar islas separadas)',
        ]}
        howToPlay="Click en una celda para alternar: Normal → Negro → Círculo (marca) → Normal"
        author="Simon Tatham"
      />

      <VictoryModal
        isOpen={victoryModalOpen}
        onClose={() => setVictoryModalOpen(false)}
        stats={{ tiempo: elapsedTime, movimientos: moves }}
      />
    </div>
  );
}
